import { sentryLogger } from '../../../monitor/monitor';

class ActivitiesStorage {
  static updatedActivities = new Map();
  static newActivities = new Map();
  static deletedActivities = new Set();
  static initialActivities = new Set();
  static initialActivitiesData = new Map();
  static taskBeingDeleted = null;
  static deletedTasksInfo = new Map();
  static hasChanges = false;
  static initialized = false;
  static eventIds = [];
  static storageRef = null;

  static init(gantt, forceReinit = false) {
    if (!gantt?.getDatastore || !gantt.initAlreadyDid) {
      return;
    }

    const tasks = gantt.getTaskByTime();

    if (tasks.length === 0) {
      return;
    }

    const shouldReinit = this._shouldReinitialize(tasks, forceReinit);

    if (this.initialized && !shouldReinit) {
      return;
    }

    this._clearStateIfNeeded(shouldReinit);
    this._initializeStorage(gantt, tasks);
  }

  static _shouldReinitialize(tasks, forceReinit) {
    if (!this.initialized) {
      return true;
    }

    if (forceReinit) {
      return true;
    }

    if (this.initialActivities.size === 0) {
      return true;
    }

    if (this.initialActivities.size !== tasks.length) {
      return true;
    }

    return false;
  }

  static _clearStateIfNeeded(shouldReinit) {
    if (this.initialized && shouldReinit) {
      this.removeEvents();
      this.initialActivities.clear();
      this.deletedActivities.clear();
      this.deletedTasksInfo.clear();
      this.updatedActivities.clear();
      this.newActivities.clear();
      this.initialized = false;
    }
  }

  static _initializeStorage(gantt, tasks) {
    const storage = gantt.getDatastore('task');
    this.storageRef = storage;
    this.initialized = true;

    this.initialActivities.clear();
    this.initialActivitiesData.clear();

    tasks.forEach((task) => {
      this.initialActivities.add(task.id);
      this.initialActivitiesData.set(task.id, task);
    });

    this._attachEvents(storage, gantt);
  }

  static _attachEvents(storage, gantt) {
    const onBeforeTaskDeleteId = storage.attachEvent(
      'onBeforeDelete',
      this._handleBeforeTaskDelete.bind(this)
    );

    const onStoreUpdatedId = storage.attachEvent(
      'onStoreUpdated',
      this._handleStoreUpdated.bind(this, gantt)
    );

    const onAfterAddId = storage.attachEvent(
      'onAfterAdd',
      this._handleAfterAdd.bind(this)
    );

    const onAfterTaskDeleteId = storage.attachEvent(
      'onAfterDelete',
      this._handleAfterTaskDelete.bind(this)
    );

    this.eventIds.push(
      onBeforeTaskDeleteId,
      onStoreUpdatedId,
      onAfterAddId,
      onAfterTaskDeleteId
    );
  }

  static _handleBeforeTaskDelete(id) {
    const parsedId = Number(id);
    this.taskBeingDeleted = this.storageRef?.pull?.[parsedId];
  }

  static _handleStoreUpdated(gantt, id, item, action) {
    if (shouldAvoidUpdateStorage(action, item, gantt)) {
      return;
    }

    this.hasChanges = true;
    const parsedId = Number(id);

    if (
      action !== 'delete' &&
      action !== 'deleted' &&
      this.deletedActivities.has(parsedId)
    ) {
      this.deletedActivities.delete(parsedId);
    }

    if (this.newActivities.has(parsedId)) {
      this.newActivities.set(parsedId, { item });
      return;
    }

    this.updatedActivities.set(parsedId, { item });
  }

  static _handleAfterAdd(id, item) {
    const parsedId = Number(id);

    if (this.deletedActivities.has(parsedId)) {
      this.deletedActivities.delete(parsedId);
    }

    if (this.updatedActivities.has(parsedId)) {
      this.updatedActivities.delete(parsedId);
    }
    this.newActivities.set(parsedId, { item });
  }

  static _handleAfterTaskDelete(id) {
    const parsedId = Number(id);
    if (this.initialActivities.has(parsedId)) {
      this.deletedActivities.add(parsedId);
      if (this.taskBeingDeleted) {
        this.deletedTasksInfo.set(parsedId, this.taskBeingDeleted);
      }
    }

    this.updatedActivities.delete(parsedId);
    this.newActivities.delete(parsedId);
    this.taskBeingDeleted = null;
  }

  static removeEvents() {
    const storage = this.storageRef;
    if (!storage) return;

    this.eventIds.forEach((eventId) => {
      storage.detachEvent(eventId);
    });
    this.eventIds = [];
  }

  static reset() {
    this.updatedActivities.clear();
    this.newActivities.clear();
    this.deletedActivities.clear();
    this.deletedTasksInfo.clear();
    this.hasChanges = false;
  }

  static reinit() {
    const gantt = window.to_use_react_gantt;
    this.reset();
    this.removeEvents();
    this.initialActivities.clear();
    this.initialized = false;

    if (gantt) {
      this.init(gantt, true);
    }
  }

  static kill() {
    try {
      this.reset();
      this.removeEvents();
      this.initialActivities.clear();
      this.initialized = false;
    } catch (e) {
      sentryLogger.error('Error on ActivitiesStorage kill', { error: e });
    } finally {
      this.initialized = false;
    }
  }

  static getUpdatedActivities() {
    return new Map(this.updatedActivities);
  }

  static getNewActivities() {
    return new Map(this.newActivities);
  }

  static getDeletedActivities() {
    return [...this.deletedActivities]
      .map((id) => this.initialActivitiesData.get(id))
      .filter((activity) => activity?.id);
  }

  static storeDefaultData(defaultData = {}) {
    if (defaultData.data.length > 0) {
      defaultData.data.forEach((item) => {
        this.newActivities.set(item.id, { item });
      });
    }
  }

  static checkHasChanges() {
    return this.hasChanges;
  }
}

function shouldAvoidUpdateStorage(action, item, gantt) {
  const { initAlreadyDid, isAutoScheduling } = gantt;

  if (!action || action === 'paint') {
    return true;
  }

  if (item === false) {
    return true;
  }

  if (!initAlreadyDid || isAutoScheduling) {
    return true;
  }

  return false;
}

export { ActivitiesStorage };
